import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sparkles } from 'lucide-react';
import Logo from './Logo';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const hasDarkBackground = !isHomePage || isScrolled;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      hasDarkBackground 
        ? 'bg-white/95 backdrop-blur-xl shadow-xl border-b border-purple-100' 
        : 'bg-gradient-to-b from-black/30 to-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo with Creative Animation */}
          <Link to="/" className="group relative">
            <div className="absolute inset-0 bg-purple-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300"></div>
            <Logo 
              textColor={hasDarkBackground ? 'text-purple-600' : 'text-white'}
              className="transition-all duration-300 transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation - Creative Layout */}
          <div className="hidden md:flex items-center space-x-2">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-5 py-3 rounded-2xl font-bold transition-all duration-300 group ${
                  location.pathname === link.path
                    ? hasDarkBackground
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-white bg-white/10 backdrop-blur-sm'
                    : hasDarkBackground
                      ? 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="relative z-10">{link.name}</span>
                {location.pathname === link.path && (
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-lg"></div>
                )}
              </Link>
            ))}
            
            {/* Creative CTA Button */}
            <Link
              to="/contact"
              className={`ml-4 relative px-7 py-3 rounded-2xl font-bold overflow-hidden group transition-all duration-300 transform hover:scale-105 ${
                hasDarkBackground
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-2xl'
                  : 'bg-white text-purple-600 shadow-xl hover:shadow-2xl'
              }`}
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Sparkles size={18} className="group-hover:rotate-180 transition-transform duration-500" />
                <span>Get Free Quote</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
          </div>

          {/* Mobile Menu Button - Creative Design */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-3 rounded-2xl transition-all duration-300 transform hover:scale-110 ${
              hasDarkBackground 
                ? 'bg-purple-50 text-purple-600 hover:bg-purple-100' 
                : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Creative Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gradient-to-br from-purple-600 via-purple-700 to-pink-700 shadow-2xl border-t border-purple-500/50">
          <div className="px-4 py-8 space-y-3">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-4 px-6 rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 ${
                  location.pathname === link.path
                    ? 'bg-white text-purple-600 shadow-xl'
                    : 'text-white hover:bg-white/10 backdrop-blur-sm'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full bg-white text-purple-600 px-6 py-4 rounded-2xl font-bold text-center shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 mt-4"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;